// CÓMO SE LEE UN CIERRE DEL DÍA YA GUARDADO, EN UN SOLO SITIO (21-10).
//
// La línea del día («Lun 24 · ★★★★☆ · Entreno ✓ · Dieta ✓ · 96 kg»), su detalle con las
// once respuestas y los textos libres. Vivían dentro de `pages/CheckInsPage.jsx` («Tus
// días», lo que ve el cliente) y la ficha del panel (`components/CoachCheckins.jsx`) tenía
// una línea suya sin detalle. Francisco, 21-10: el entrenador abre cada día y ve todo lo que
// contestó, igual que lo ve el cliente. Las dos pantallas leen de aquí.
//
// Las etiquetas de cada respuesta (entreno, cardio, suplementos) vienen de
// `lib/respuestasDelCierre`, que es donde están los botones.

import { hoyLocal } from './horaEspana';
import {
    ENTRENO_VALOR, CARDIO_VALOR, SUPLES_VALOR, RENDIMIENTO_VALOR, SENSACIONES_ENTRENO_VALOR,
    ACTIVIDAD_VALOR, HORAS_SUENO_VALOR, CANTIDAD_COMIDA_VALOR, entrenoDeHoy,
} from './respuestasDelCierre';

// ── Cómo se nombra cada respuesta ────────────────────────────────────────────
//
// UN SOLO VOCABULARIO CON EL FORMULARIO: el historial decía «Desgaste: menos de lo
// habitual» mientras la pregunta habla de moverse y sus botones dicen «Menos» / «Como
// siempre» / «Más». Eran dos maneras de nombrar el mismo dato, y el cliente leía en su
// historial una palabra que nadie le había preguntado. Los valores guardados
// (menos/igual/mas) no cambian: esto es solo cómo se le enseña.
export const MOVIMIENTO_VALOR = { menos: 'Menos', igual: 'Como siempre', mas: 'Más' };
// La línea del día, con los tres estados del punto 19:
//   entrenó -> ✓ · le tocaba y no fue -> ✗ · tocaba descanso -> «Descanso», SIN SÍMBOLO,
//   «porque no es ni bueno ni malo».
// Y LAS DOS DEL 26-10: «Al 90 %» entrenó (✓) y «a medias» se dice tal cual.
export const ENTRENO_LINEA = {
    si: 'Entreno ✓', adaptado: 'Entreno ✓', a_medias: 'Entreno a medias',
    no: 'Entreno ✗', descanso: 'Descanso',
};

export const NOMBRE_COMIDA = (k) => {
    if (!k) return 'Comida';
    if (k === 'Post') return 'Post-entreno';
    if (k === 'Intra') return 'Intra-entreno';
    return k.startsWith('C') ? `Comida ${k.slice(1)}` : k;
};

// «★★★★☆». Para leer de un vistazo una fila de días, que es para lo que Jesús las pidió.
export const estrellitas = (n) => '★'.repeat(Math.max(0, Math.min(5, n || 0)))
    + '☆'.repeat(Math.max(0, 5 - Math.max(0, Math.min(5, n || 0))));

// «77,3 kg». Los decimales con coma, que es como se escriben aquí.
export const kilos = (v) => `${String(v).replace('.', ',')} kg`;

// «hoy», «ayer» o «20 de agosto», con el reloj de quien lo mira.
export const cuando = (iso, hoyIso = hoyLocal()) => {
    if (!iso) return '';
    const dias = Math.round((new Date(`${hoyIso}T12:00:00`) - new Date(`${iso}T12:00:00`)) / 86400000);
    if (dias <= 0) return 'hoy';
    if (dias === 1) return 'ayer';
    return new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};

// «Lun 24». La cabecera de una línea del historial (punto 19).
//
// Y CON SU MES CUANDO NO ES ESTE (Jesús, 11-09, punto 18: «las fechas sueltas, con su mes:
// Lun 24 de agosto»). Dentro del mes en curso «Lun 24» se entiende solo y añadir el mes a
// las siete filas es ruido; en cuanto se sale del mes, el número solo no dice cuándo fue.
export const diaCorto = (iso, hoyIso) => {
    const d = new Date(`${iso}T12:00:00`);
    const nombre = d.toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '');
    const corto = `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${d.getDate()}`;
    const esteMes = (hoyIso || '').slice(0, 7) === String(iso).slice(0, 7);
    return esteMes ? corto : `${corto} de ${d.toLocaleDateString('es-ES', { month: 'long' })}`;
};

// El día de un cierre, 'AAAA-MM-DD'. Los de antes del bloque F no traen `dia`: su día sale
// del created_at, que el navegador ya pasa a la hora de quien lo mira.
export const diaDelCierre = (c) =>
    c?.dia || (c?.created_at ? new Date(c.created_at).toLocaleDateString('en-CA') : null);

// «NO ENTRENÓ» SOLO CUANDO CONTESTÓ QUE NO (P69, doc 23-08). `trained` dejó de preguntarse
// el 31-07 y en los check-ins viejos venía a false por defecto: un false a secas no es un
// «no», es que no consta, y así se dice (en gris). Un true sí se respeta: alguien lo marcó.
// La respuesta de verdad viaja en `entreno_respuesta`.
export const ENTRENO_SIN_REGISTRO = 'Entreno: sin registro';

// «Lun 24 · ★★★★☆ · Entreno ✓ · Dieta ✓ · 96 kg»: las cuatro cosas del punto 19, en una
// sola línea y por día. El peso, SOLO el día que lo registró.
export const lineaDelDia = (c) => {
    const trozos = [];
    if (c.sensaciones != null) trozos.push(estrellitas(c.sensaciones));
    if (c.entreno_respuesta) trozos.push(ENTRENO_LINEA[entrenoDeHoy(c.entreno_respuesta)] || 'Entreno');
    else if (c.trained != null) trozos.push(c.trained ? 'Entreno ✓' : ENTRENO_SIN_REGISTRO);
    if (c.nutrition_followed != null) trozos.push(c.nutrition_followed ? 'Dieta ✓' : 'Dieta ✗');
    if (c.weight != null) trozos.push(kilos(c.weight));
    return trozos;
};

// El detalle de un día (punto 21): las once, «con el nombre entero» y «3 de 5», no «3/5»,
// que «se lee, no se descifra».
//
// LO QUE ESE DÍA NO SE PREGUNTÓ, NO SALE. Los cierres de antes del 24-08 no tienen
// sensaciones, ni cardio, ni el entreno de tres estados: enseñarlos en blanco o con un
// guion sería once renglones para decir que no hay nada. Sale lo que contestó y ya.
export const detalleDelDia = (c, hoyIso = hoyLocal()) => {
    const filas = [];
    // `apagado`: lo que no es una respuesta sino que no consta; las pantallas lo pintan en gris.
    const pon = (nombre, valor, apagado = false) => {
        if (valor) filas.push(apagado ? { nombre, valor, apagado } : { nombre, valor });
    };
    // En el orden del check-in del 26-10. `sensaciones` ya no se pregunta: sale en los días
    // de antes que la tienen.
    pon('Sensaciones', c.sensaciones != null ? estrellitas(c.sensaciones) : null);
    pon('Horas de sueño', HORAS_SUENO_VALOR[c.horas_sueno]);
    pon('Descanso', c.descanso != null ? `${c.descanso} de 5` : null);
    if (c.entreno_respuesta || c.trained === true) {
        const respuesta = c.entreno_respuesta
            ? (ENTRENO_VALOR[c.entreno_respuesta] || c.entreno_respuesta)
            : 'Sí';
        // Con `rendimiento` las estrellas las puso el servidor (se deducen de él): se dice
        // la frase que eligió y no unas estrellas que no marcó.
        pon('Entreno', c.entreno_estrellas != null && !c.rendimiento
            ? `${respuesta} ${estrellitas(c.entreno_estrellas)}` : respuesta);
    } else if (c.trained === false) {
        pon('Entreno', 'Sin registro', true);       // P69: ver ENTRENO_SIN_REGISTRO
    }
    pon('Rendimiento', RENDIMIENTO_VALOR[c.rendimiento]);
    pon('Sensaciones entrenando', SENSACIONES_ENTRENO_VALOR[c.sensaciones_entreno]);
    pon('Energía', c.energy != null ? `${c.energy} de 5` : null);
    pon('Cardio', CARDIO_VALOR[c.cardio]);
    // Lo mismo con el movimiento: con `actividad`, el `movimiento` es suyo, no del cliente.
    if (c.actividad) pon('Actividad', ACTIVIDAD_VALOR[c.actividad]);
    else pon('Movimiento', MOVIMIENTO_VALOR[c.movimiento]);
    pon('Cantidad de comida', CANTIDAD_COMIDA_VALOR[c.cantidad_comida]);
    pon('Hambre', c.hunger_anxiety != null ? `${c.hunger_anxiety} de 5` : null);
    if (c.extras_respuesta) pon('Extras', c.extras_respuesta === 'si' ? 'Sí' : 'No');
    pon('Suplementos', SUPLES_VALOR[c.suplementos?.respuesta]);
    pon('Ánimo', c.mood != null ? `${c.mood} de 5` : null);      // los cierres viejos
    if (c.nutrition_followed != null) pon('Dieta', c.nutrition_followed ? 'Cerrada' : 'Sin cerrar');
    if (c.cena_hecha != null && c.comida_pendiente) {
        pon(NOMBRE_COMIDA(c.comida_pendiente), c.cena_hecha ? 'La hizo' : 'Sin registrar');
    }
    // El peso ya no es siempre del día del cierre: la casilla pregunta de cuándo es el
    // pesaje (doc 24-08), así que si lo apuntó de otro día se dice, o el detalle del 24
    // estaría fechando un peso del 22.
    if (c.weight != null) {
        const otroDia = c.peso_fecha && c.peso_fecha !== c.dia ? ` · ${cuando(c.peso_fecha, hoyIso)}` : '';
        pon('Peso', `${kilos(c.weight)}${otroDia}`);
    }
    if (c.notas?.texto) pon('Notas', c.notas.compartida ? 'Compartidas' : 'Solo para ti');
    return filas;
};

// Los textos libres del cierre, cada uno con su título delante. Al equipo las notas «Solo
// para ti» ya le llegan quitadas del servidor (`core/notas_del_cierre`): aquí no se decide.
export const renglonesDelCierre = (c) => {
    const renglones = [];
    if (c.entreno_nota) renglones.push({ titulo: 'Entreno', texto: c.entreno_nota });
    if (c.suplementos?.detalle) renglones.push({ titulo: 'Suplementos', texto: c.suplementos.detalle });
    if (c.exceso_nota) renglones.push({ titulo: 'Sobre el exceso', texto: c.exceso_nota });
    if (c.comido_hoy) renglones.push({ titulo: 'Comido hoy', texto: c.comido_hoy });
    if (c.notas?.texto) {
        renglones.push({
            titulo: c.notas.compartida ? 'Notas · compartidas' : 'Notas · solo para ti',
            texto: c.notas.texto,
        });
    }
    return renglones;
};
